import { z } from 'zod';
import { commissionRuleSchema, retailerSparkFiltersSchema } from '../spark/spark.core';
import { PAYMENT_METHOD_TYPES } from '../pos';
import { objectIdRegex } from '../util/regex';
import { AccountMarkets } from '../account';

/**
 * Bulk sparks payloads for the Spark Wizard (vendor bulk flow).
 *
 * NOTE: This is intentionally a *structural* schema for phase 1:
 * - We validate that the payload shape is correct and has required keys.
 * - We avoid deeper business validation (eligibility, entitlements, etc.).
 * - `masterSpark` is permissive (`passthrough`) to avoid coupling to Spark model changes.
 */

export const bulkSparksModeSchema = z.enum(['create', 'request']);
export type BulkSparksMode = z.infer<typeof bulkSparksModeSchema>;

/**
 * Properties that should never be included in `masterSpark` for bulk payloads.
 *
 * Shared between:
 * - UI: `omit(context.spark, BULK_MASTER_SPARK_OMITTED_PROPERTIES)`
 * - API/lib: guardrails to ensure per-retailer / derived fields don't leak into masterSpark
 */
export const BULK_MASTER_SPARK_OMITTED_PROPERTIES = [
  'groupId',
  'locationIds',
  'posEmployeeProfileIds',
  'participantFilters',
  'posProductIds',
  'retailerFilters',
  'vendorFilters',
] as const;

/**
 * Shared spark fields across all retailers.
 *
 * Intentionally excludes per-retailer fields like:
 * - groupId
 * - locationIds / participant IDs
 * - retailerFilters (these are provided per retailer)
 * - posProductIds (rules-based; can be derived later)
 */
export const bulkSparksMasterSparkSchema = z
  .object({
    /**
     * Vendor account initiating the bulk action.
     *
     */
    originatorGroupId: z.string().regex(objectIdRegex),

    /**
     * Vendors must pick a brand in bulk mode; this is required.
     * Used by rules-based product logic.
     */
    sparkBrandId: z.string().regex(objectIdRegex),

    markets: z.array(z.enum(AccountMarkets as [string, ...string[]])).min(1),

    name: z.string().min(1),
    description: z.string().optional(),
    type: z.string().min(1),
    // In practice we always send a metric (commission sparks default to total_units in UI).
    metric: z.string().min(1),
    detailedSparkType: z.string().min(1),
    emoji: z.string().optional(),
    startDate: z.string().min(1),
    endDate: z.string().min(1),

    // Common optional fields that may exist on spark payloads
    awards: z.any().optional(),
    goals: z.any().optional(),
    commissions: z.any().optional(),
    commissionRules: z.any().optional(),
    fulfillmentTypes: z.any().optional(),
    minimumThresholdToQualify: z.any().optional(),
    minimumTransactionsToQualify: z.any().optional(),
    splitPayout: z.boolean().default(false),
    percentIncreaseData: z.any().optional(),
    paymentMethod: z.enum(PAYMENT_METHOD_TYPES).optional(),
    omitPreorders: z.boolean().optional(),
  })
  .passthrough()
  .superRefine((obj, ctx) => {
    // Guardrails: ensure we never accidentally embed per-retailer fields into masterSpark.
    BULK_MASTER_SPARK_OMITTED_PROPERTIES.forEach((key) => {
      if (key in obj) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `masterSpark must not include '${key}' (it is derived per retailer or not used in bulk)`,
        });
      }
    });
  });
export type BulkSparksMasterSpark = z.infer<typeof bulkSparksMasterSparkSchema>;

/**
 * Per-retailer commission rules schema for bulk sparks.
 * Supports fixed (same value for all products), manual (per-product values),
 * and rules-based (condition-driven per-rule values) types.
 */
export const bulkCommissionRulesSchema = z
  .object({
    type: z.enum(['fixed', 'manual', 'rules']),
    fixedValue: z.number().optional(),
    items: z.array(commissionRuleSchema).optional(),
  })
  .optional();
export type BulkCommissionRules = z.infer<typeof bulkCommissionRulesSchema>;

export const bulkRetailerSpecSchema = z.object({
  retailerAccountId: z.string().min(1),
  locationIds: z.array(z.string().min(1)).min(1),
  retailerFilters: retailerSparkFiltersSchema,

  // Per-retailer commission configuration (for commission sparks)
  commissionRules: bulkCommissionRulesSchema,
  commissions: z.array(z.any()).optional(), // Manual per-product commissions
  minimumThresholdToQualify: z.number().optional(),
});
export type BulkRetailerSpec = z.infer<typeof bulkRetailerSpecSchema>;

export const bulkSparksRequestBodySchema = z.object({
  mode: bulkSparksModeSchema,
  masterSpark: bulkSparksMasterSparkSchema,
  retailers: z.array(bulkRetailerSpecSchema).min(1),
  /**
   * Optional: ID of the RequestForSpark being accepted.
   * When provided, the request status will be updated to 'accepted' after spark creation.
   * This is used when a vendor accepts a retailer's request for a spark.
   */
  requestForSparkId: z.string().regex(objectIdRegex).optional(),
  /**
   * Optional: Frontend-generated UUID for analytics event correlation.
   * Used to link Spark Bulk Flow Started, Spark Bulk Submitted, and Spark Bulk Completed events.
   * If not provided, the backend will generate its own batchId.
   */
  bulkOperationId: z.string().uuid().optional(),
});
export type BulkSparksRequestBody = z.infer<typeof bulkSparksRequestBodySchema>;

export const bulkSparksResponseBodySchema = z.object({
  ok: z.literal(true),
  sparkIds: z.array(z.string().min(1)),
  byRetailer: z.record(z.string().min(1), z.string().min(1)),
  batchId: z.string().min(1),
});
export type BulkSparksResponseBody = z.infer<typeof bulkSparksResponseBodySchema>;
